import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ReviewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  animation: ${fadeIn} 0.4s ease-out;
  color: var(--mp-textColor, #171717);
`;

export const ReviewsHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`;

export const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const BigRating = styled.div`
  font-family: 'Liberation Serif', serif;
  font-size: 48px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  line-height: 1;
`;

export const RatingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StarRow = styled.div`
  display: flex;
  gap: 2px;
`;

export const ReviewCountText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-featureDescColor, #71717a);
`;

export const WriteReviewBtn = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(201, 77, 170, 0.3);
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const WriteReviewFormContainer = styled.div`
  background: var(--mp-cardBackground, rgba(255,255,255,0.8));
  border: 1px solid var(--mp-border, rgba(0,0,0,0.1));
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeIn} 0.3s ease-out;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(255,255,255,0.03);
    border-color: rgba(255,255,255,0.08);
  }
`;

export const FormLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

export const StarPickerRow = styled.div`
  display: flex;
  gap: 4px;
  cursor: pointer;
`;

export const TextInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--mp-border, rgba(0,0,0,0.15));
  background: transparent;
  color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  box-sizing: border-box;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.15);
    color: #fff;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--mp-border, rgba(0,0,0,0.15));
  background: transparent;
  color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  box-sizing: border-box;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.15);
    color: #fff;
  }
`;

export const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

export const CancelBtn = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 1px solid var(--mp-border, rgba(0,0,0,0.2));
  background: transparent;
  color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: rgba(0,0,0,0.05); }

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.2);
    color: #fff;
    &:hover { background: rgba(255,255,255,0.05); }
  }
`;

export const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const ReviewCard = styled.div`
  padding: 20px 0;
  border-bottom: 1px solid var(--mp-border, rgba(0,0,0,0.08));
  display: flex;
  flex-direction: column;
  gap: 12px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.08);
  }

  &:last-child { border-bottom: none; }
`;

export const ReviewerMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ReviewerAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #333;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 14px;
`;

export const ReviewerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ReviewerName = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

export const ReviewDate = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: var(--mp-featureDescColor, #71717a);
`;

export const ReviewTitle = styled.h4`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

export const ReviewBodyText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--mp-textColor, #3f3f46);

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #d4d4d8;
  }
`;

export const OwnerReplyBlock = styled.div`
  margin-top: 12px;
  padding: 16px;
  border-radius: 0 8px 8px 0;
  border-left: 3px solid #c94daa;
  background: rgba(201, 77, 170, 0.04);
  display: flex;
  flex-direction: column;
  gap: 6px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(201, 77, 170, 0.08);
  }
`;

export const OwnerReplyHeader = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #c94daa;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const OwnerReplyText = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: var(--mp-textColor, #3f3f46);

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #d4d4d8;
  }
`;

export const ReviewActionsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

export const TextBtn = styled.button`
  background: transparent;
  border: none;
  color: var(--mp-featureDescColor, #71717a);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;

  &:hover { color: #c94daa; }
`;

export const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

export const LoadMoreBtn = styled.button`
  padding: 10px 24px;
  border-radius: 100px;
  border: 1.5px solid var(--mp-border, rgba(0,0,0,0.2));
  background: transparent;
  color: var(--mp-textColor, #171717);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: rgba(0,0,0,0.05); }

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255,255,255,0.2);
    color: #fff;
    &:hover { background: rgba(255,255,255,0.05); }
  }
`;

export const EmptyReviews = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  gap: 12px;
  background: rgba(0,0,0,0.02);
  border-radius: 12px;
  border: 1px dashed var(--mp-border, rgba(0,0,0,0.1));

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(255,255,255,0.02);
    border-color: rgba(255,255,255,0.1);
  }
`;

export const EmptyEmoji = styled.div`
  font-size: 32px;
`;
export const EmptyTitle = styled.h4`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: var(--mp-headingColor, #171717);
`;
export const EmptyDesc = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-featureDescColor, #71717a);
`;
